import { Instant, LocalDate } from "@js-joda/core"
import BloodGlucoseThresholds from "./BloodGlucoseThresholds"
import BloodPressureThresholds from "./BloodPressureThresholds"
import { EnrollmentStatus } from "./CustomerSupport"
import { DeviceOrder, DeviceOrderDto, toDeviceOrder } from "./DeviceOrders"

type CommonPrescriptionFields = {
  id: string
  organizationId: string
  patientId: string
  pregnancyId?: string
  type: ClinicalProtocolType
  clinicalProtocolName: string
  status: PrescriptionStatus
  startDate: LocalDate
  endDate?: LocalDate
  diagnosis?: string
  firstReadingTime?: Instant
  lastReadingTime?: Instant
  patientOptedInClinically: boolean
  estimatedDateOfDelivery?: LocalDate
  caregiverId?: string
  caregiverDepartmentId?: string
  deviceDistributionMethod: DeviceDistributionMethod
  deviceOrders: DeviceOrder[]
  offboarding?: {
    reason: OffboardReason
    note?: string
    offboardedBy: string
    offboardedAt: Instant
    source: OffboardingSource
  }
}

export type OffboardingSource = "clinician" | "support"

export type HypertensionPrescription = CommonPrescriptionFields & {
  type: "hypertension"
  thresholds: BloodPressureThresholds
  cuffSize: BloodPressureCuffSize
}

export type DiabetesPrescription = CommonPrescriptionFields & {
  type: "diabetes"
  thresholds: BloodGlucoseThresholds
}

export type Prescription = HypertensionPrescription | DiabetesPrescription

export type BloodPressureCuffSize = "S" | "M" | "L" | "XL" | "XXL" | "PENDING"
export type ClinicalProtocolType = "hypertension" | "diabetes"
export type PrescriptionStatus = "active" | "inactive"

export type OffboardReason =
  | "device-connectivity-issue"
  | "patient-completed-protocol"
  | "patient-end-program-early"
  | "patient-non-compliant"
  | "patient-transferred"
  | "patient-declined"
  | "other"

export const deviceDistributionMethods = [
  "home-delivery-or-reuse",
  "from-provider",
] as const

export type DeviceDistributionMethod =
  (typeof deviceDistributionMethods)[number]

export type CommonPrescriptionApiDtoFields = {
  id: string
  organizationId: string
  patientId: string
  clinicalProtocolName: string
  pregnancyId?: string
  status: PrescriptionStatus
  diagnosis?: string
  startDate: string
  endDate?: string
  firstReadingTime?: string
  lastReadingTime?: string
  patientOptedInClinically: boolean
  estimatedDateOfDelivery?: string
  caregiverId?: string
  caregiverLocationId?: string
  deviceDistributionMethod: DeviceDistributionMethod
  deviceOrders: DeviceOrderDto[]
  offboarding?: {
    reason: OffboardReason
    note?: string
    offboardedBy: string
    offboardedAt: string
    source: OffboardingSource
  }
}

export type DiabetesPrescriptionApiDto = CommonPrescriptionApiDtoFields & {
  type: "diabetes"
  bloodGlucoseThresholds: BloodGlucoseThresholds
}

export type HypertensionPrescriptionApiDto = CommonPrescriptionApiDtoFields & {
  type: "hypertension"
  bloodPressureThresholds: BloodPressureThresholds
  cuffSize: BloodPressureCuffSize
}

export type PrescriptionApiDto =
  DiabetesPrescriptionApiDto | HypertensionPrescriptionApiDto

export const toPrescription = (
  dto: PrescriptionApiDto
): DiabetesPrescription | HypertensionPrescription => ({
  ...(dto.type === "diabetes"
    ? {
        ...dto,
        thresholds: dto.bloodGlucoseThresholds,
      }
    : {
        ...dto,
        thresholds: dto.bloodPressureThresholds,
      }),
  startDate: LocalDate.parse(dto.startDate),
  endDate: dto.endDate ? LocalDate.parse(dto.endDate) : undefined,
  estimatedDateOfDelivery: dto.estimatedDateOfDelivery
    ? LocalDate.parse(dto.estimatedDateOfDelivery)
    : undefined,
  firstReadingTime: dto.firstReadingTime
    ? Instant.parse(dto.firstReadingTime)
    : undefined,
  lastReadingTime: dto.lastReadingTime
    ? Instant.parse(dto.lastReadingTime)
    : undefined,
  offboarding: dto.offboarding
    ? {
        ...dto.offboarding,
        offboardedAt: Instant.parse(dto.offboarding.offboardedAt),
      }
    : undefined,
  deviceOrders: dto.deviceOrders.map(toDeviceOrder),
})

export type CreateDiabetesPatientPrescriptionDto = {
  type: "diabetes"
  clinicalProtocolName: string
  bloodGlucoseThresholds: BloodGlucoseThresholds
  diagnosis?: string
  startDate: string
  caregiverId?: string
  caregiverDepartmentId?: string
  deviceDistributionMethod: DeviceDistributionMethod
}

export type CreateHypertensionPatientPrescriptionDto = {
  type: "hypertension"
  clinicalProtocolName: string
  bloodPressureThresholds: BloodPressureThresholds
  diagnosis?: string
  startDate: string
  caregiverId?: string
  caregiverDepartmentId?: string
  deviceDistributionMethod: DeviceDistributionMethod
  cuffSize?: BloodPressureCuffSize
}

export type CreateDiabetesPrescriptionDto =
  CreateDiabetesPatientPrescriptionDto & {
    pregnancyId: string
  }

export type CreateHypertensionPrescriptionDto =
  CreateHypertensionPatientPrescriptionDto & {
    pregnancyId: string
  }

export type UpdateHypertensionPrescriptionDto = Omit<
  CreateHypertensionPrescriptionDto,
  "type" | "clinicalProtocolName" | "bloodPressureThresholds" | "pregnancyId"
> & {
  endDate?: string
  enrollmentStatus?: EnrollmentStatus
}

export type UpdateDiabetesPrescriptionDto = Omit<
  CreateDiabetesPrescriptionDto,
  "type" | "clinicalProtocolName" | "bloodGlucoseThresholds" | "pregnancyId"
> & {
  endDate?: string
  enrollmentStatus?: EnrollmentStatus
}
